import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRightIcon, LoaderIcon } from 'lucide-react';
import { reviewSummary } from '../data/site';

// Exact hosted frame and resize library emitted by SureCritic's account-generated
// widget 8179. React owns the frame so the vendor injector cannot race route changes.
const FRAME_SRC = 'https://www.surecritic.com/businesses/162110/plugins/review_plugins/8179';
const RESIZER_SRC = 'https://dd5cthgnln9mv.cloudfront.net/assets/iframe-resizer-outer-b8a50640b1bc695c76c0d05f18c5870b6124ef816c4753779ad43097619d53b8.js';
type Status = 'loading' | 'ready' | 'error';
type ReviewFrame = HTMLIFrameElement & {
  iFrameResizer?: {
    removeListeners: () => void;
  };
};
type ResizeOptions = {
  checkOrigin: string[];
  sizeWidth: boolean;
  heightCalculationMethod: string;
  onInit: () => void;
  onResized: () => void;
};
type ResizeFunction = (options: ResizeOptions, iframe: HTMLIFrameElement) => unknown;
type ResizeWindow = Window & {
  iFrameResize?: ResizeFunction;
};
let resizeLibrary: Promise<ResizeFunction> | null = null;
function loadResizeLibrary(): Promise<ResizeFunction> {
  const existing = (window as ResizeWindow).iFrameResize;
  if (existing) return Promise.resolve(existing);
  if (resizeLibrary) return resizeLibrary;
  resizeLibrary = new Promise<ResizeFunction>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'east-end-surecritic-resizer';
    script.src = RESIZER_SRC;
    script.async = true;
    const cleanHandlers = () => {
      script.onload = null;
      script.onerror = null;
    };
    const fail = () => {
      cleanHandlers();
      script.remove();
      resizeLibrary = null;
      reject(new Error('SureCritic resize library could not be loaded'));
    };
    script.onload = () => {
      const resize = (window as ResizeWindow).iFrameResize;
      if (!resize) {
        fail();
        return;
      }
      cleanHandlers();
      resolve(resize);
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return resizeLibrary;
}
export function SureCriticReviews() {
  const frameRef = useRef<ReviewFrame>(null);
  const [status, setStatus] = useState<Status>('loading');
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    let cancelled = false;
    setStatus('loading');
    const timeout = window.setTimeout(() => {
      if (!cancelled) setStatus('error');
    }, 15000);
    const onReady = () => {
      if (cancelled) return;
      window.clearTimeout(timeout);
      setStatus('ready');
    };
    loadResizeLibrary().then(resize => {
      if (cancelled || !frame.isConnected) return;
      resize({
        checkOrigin: ['https://www.surecritic.com'],
        sizeWidth: false,
        heightCalculationMethod: 'lowestElement',
        onInit: onReady,
        onResized: onReady
      }, frame);
    }).catch(() => {
      if (cancelled) return;
      window.clearTimeout(timeout);
      setStatus('error');
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      frame.iFrameResizer?.removeListeners();
    };
  }, []);
  return <div className="w-full min-w-0" data-id="element-448" data-mp="1qtv128b7kkln">
      <div className="w-full min-w-0 rounded-lg border border-black/10 bg-white p-0 sm:p-6" aria-busy={status === 'loading'} data-id="element-449" data-mp="1qstou0ighs2c">
        <div id="surecritic-reviews" className="w-full min-w-0" data-id="element-450" data-mp="1hswvhll4g50y">
          <iframe id="sc-review-plugin" ref={frameRef} src={FRAME_SRC} title="Verified customer reviews for East End Automotive Repair on SureCritic" className="block h-px max-w-full" style={{
          width: '1px',
          minWidth: '100%',
          border: 0,
          visibility: status === 'ready' ? 'visible' : 'hidden'
        }} scrolling="no" data-id="element-451" data-mp="1hurhl384q36m" />
        </div>
        {status === 'loading' && <p role="status" className="flex items-center gap-2.5 py-10 text-[15px] text-steel/70" data-id="element-452" data-mp="175d8no888a7d">
            <LoaderIcon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" data-id="element-453" data-mp="12690n0x1giim" />
            Loading verified reviews…
          </p>}
        {status === 'error' && <p role="status" className="max-w-[560px] py-8 text-[15px] leading-relaxed text-steel/75" data-id="element-454" data-mp="y6rue59sfhce">
            Our verified reviews could not be loaded right now. You can read the full profile directly on SureCritic.
          </p>}
      </div>
      <a href={reviewSummary.url} target="_blank" rel="noreferrer" className="mt-5 inline-flex max-w-full items-center gap-2 text-[15px] font-semibold text-steel underline decoration-steel/40 underline-offset-4 hover:text-ink" data-id="element-455" data-mp="jeq0r2yb9kkt">
        View reviews on SureCritic
        <ArrowUpRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-456" data-mp="5eyg4jdah5xu" />
      </a>
    </div>;
}